import { useEffect, useMemo, useState } from "react";
import {
  BellRing,
  CheckCircle2,
  ChevronDown,
  Clock3,
  FileText,
  Mail,
  MessageCircleMore,
  Smartphone,
} from "lucide-react";
import {
  CHANNEL_LABELS,
  encodeCuijPath,
  EVENT_LABELS,
  EVENT_TYPES,
} from "@tribunalea/shared";
import { useApi } from "../App.jsx";
import { StatusBadge } from "../components/StatusBadge.jsx";
import { AlertsSectionNav } from "../components/AlertsSectionNav.jsx";
import { AppLink, CopyableDate, PageHeader } from "@tribunalea/ui";

const icons = {
  email: Mail,
  sms: Smartphone,
  whatsapp: MessageCircleMore,
};

export function DeliveriesPage() {
  const api = useApi();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  useEffect(() => {
    api
      .deliveries()
      .then(setDeliveries)
      .finally(() => setLoading(false));
  }, []);

  const delivered = useMemo(
    () => deliveries.filter((item) => item.status === "delivered").length,
    [deliveries],
  );

  function toggleDetails(deliveryId) {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(deliveryId)) next.delete(deliveryId);
      else next.add(deliveryId);
      return next;
    });
  }

  return (
    <div className="page">
      <PageHeader
        eyebrow="Praxis · Alertas"
        title="Historial de envíos"
        description="Revisá qué se envió, por qué canal y con qué resultado."
      />

      <AlertsSectionNav />

      <section className="history-stats">
        <div>
          <span className="stat-icon emerald-soft">
            <CheckCircle2 size={19} />
          </span>
          <span>
            <strong>{delivered}</strong>
            <small>Entregas confirmadas</small>
          </span>
        </div>
        <div>
          <span className="stat-icon slate-soft">
            <Clock3 size={19} />
          </span>
          <span>
            <strong>{deliveries.length}</strong>
            <small>Envíos registrados</small>
          </span>
        </div>
      </section>

      <section className="history-card">
        <div className="history-header">
          <strong>Actividad reciente</strong>
          <span>Últimos 50 registros</span>
        </div>
        {loading ? (
          <div className="loading-list">
            <span />
            <span />
          </div>
        ) : deliveries.length ? (
          <div className="history-list">
            {deliveries.map((delivery) => {
              const Icon = icons[delivery.channel] || Mail;
              const expedientes = delivery.payload?.expedientes || [];
              const integrationNotifications =
                delivery.payload?.integrationNotifications || [];
              const isIntegrationNotification =
                delivery.eventType ===
                EVENT_TYPES.NEW_INTEGRATION_NOTIFICATION;
              const itemCount = delivery.itemCount
                || (isIntegrationNotification
                  ? integrationNotifications.length
                  : delivery.expedienteCount);
              const expanded = expandedIds.has(delivery._id);
              const detailsId = `delivery-details-${delivery._id}`;
              return (
                <article
                  key={delivery._id}
                  className={`history-entry ${expanded ? "is-expanded" : ""}`}
                >
                  <div className="history-entry-summary">
                    <span className={`delivery-icon channel-${delivery.channel}`}>
                      <Icon size={18} />
                    </span>
                    <span className="delivery-copy">
                      <strong>{EVENT_LABELS[delivery.eventType]}</strong>
                      <small>
                        {itemCount}{" "}
                        {isIntegrationNotification
                          ? itemCount === 1
                            ? "notificación"
                            : "notificaciones"
                          : itemCount === 1
                            ? "expediente"
                            : "expedientes"} ·{" "}
                        {CHANNEL_LABELS[delivery.channel]}
                      </small>
                      {expedientes.length > 0 && (
                        <span className="delivery-cuij-preview">
                          {expedientes.slice(0, 3).map((expediente) => (
                            <AppLink
                              key={expediente.cuij}
                              to={`/expedientes/${encodeCuijPath(expediente.cuij)}`}
                              activeClassName=""
                            >
                              {expediente.cuij}
                            </AppLink>
                          ))}
                          {expedientes.length > 3 && (
                            <em>+{expedientes.length - 3}</em>
                          )}
                        </span>
                      )}
                    </span>
                    <time>
                      <CopyableDate value={delivery.createdAt}>
                        {formatRelative(delivery.createdAt)}
                      </CopyableDate>
                    </time>
                    <StatusBadge status={delivery.status} />
                    {expedientes.length > 0
                    || integrationNotifications.length > 0 ? (
                      <button
                        type="button"
                        className="history-details-toggle"
                        aria-label={
                          expanded
                            ? "Ocultar expedientes del envío"
                            : "Ver expedientes del envío"
                        }
                        aria-expanded={expanded}
                        aria-controls={detailsId}
                        onClick={() => toggleDetails(delivery._id)}
                      >
                        <ChevronDown size={17} />
                      </button>
                    ) : (
                      <span className="history-details-toggle-spacer" />
                    )}
                  </div>
                  {expanded && (
                    <div className="delivery-details" id={detailsId}>
                      <div className="delivery-details-heading">
                        <span>
                          {isIntegrationNotification
                            ? <BellRing size={15} />
                            : <FileText size={15} />}
                          {isIntegrationNotification
                            ? "Notificaciones incluidas"
                            : "Expedientes incluidos"}
                        </span>
                        {delivery.payload?.ruleName && (
                          <small>Alerta “{delivery.payload.ruleName}”</small>
                        )}
                      </div>
                      <div className="delivery-expediente-list">
                        {integrationNotifications.map((notification) => (
                          <div
                            className="delivery-expediente"
                            key={notification.externalId}
                          >
                            <div className="delivery-expediente-heading">
                              <strong>
                                {notification.title
                                  || "Notificación del PJM"}
                              </strong>
                            </div>
                            <small>
                              {[
                                notification.expediente
                                  ? `Expediente ${notification.expediente}`
                                  : "",
                                notification.foja
                                  ? `Foja ${notification.foja}`
                                  : "",
                                notification.organismo,
                              ].filter(Boolean).join(" · ")}
                            </small>
                          </div>
                        ))}
                        {expedientes.map((expediente) => {
                          const actuaciones =
                            expediente.newActuaciones || [];
                          const stageChange = expediente.stageChange;
                          return (
                            <div
                              className="delivery-expediente"
                              key={expediente.cuij}
                            >
                              <div className="delivery-expediente-heading">
                                <AppLink
                                  to={`/expedientes/${encodeCuijPath(expediente.cuij)}`}
                                  activeClassName=""
                                >
                                  {expediente.cuij}
                                </AppLink>
                                {stageChange ? (
                                  <span>Cambio de etapa</span>
                                ) : (
                                  <span>
                                    {actuaciones.length}{" "}
                                    {actuaciones.length === 1
                                      ? "actuación nueva"
                                      : "actuaciones nuevas"}
                                  </span>
                                )}
                              </div>
                              <strong>
                                {expediente.caratula ||
                                  "Carátula sin informar"}
                              </strong>
                              {expediente.organismoActual && (
                                <small>{expediente.organismoActual}</small>
                              )}
                              {stageChange && (
                                <div className="delivery-stage-change">
                                  <span>
                                    {stageChange.fromLabel ||
                                      "Etapa anterior"}
                                  </span>
                                  <strong>→</strong>
                                  <span>
                                    {stageChange.toLabel ||
                                      "Nueva etapa"}
                                  </span>
                                </div>
                              )}
                              {actuaciones.length > 0 && (
                                <ul>
                                  {actuaciones
                                    .slice(0, 5)
                                    .map((actuacion, index) => (
                                      <li
                                        key={
                                          actuacion.mexpId ||
                                          actuacion.numero ||
                                          `${expediente.cuij}-${index}`
                                        }
                                      >
                                        {actuacion.titulo ||
                                          actuacion.tipo ||
                                          "Actuación sin título"}
                                      </li>
                                    ))}
                                  {actuaciones.length > 5 && (
                                    <li>
                                      +{actuaciones.length - 5} actuaciones
                                      más
                                    </li>
                                  )}
                                </ul>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        ) : (
          <div className="no-results">
            <Clock3 size={23} />
            <h2>No hay envíos todavía</h2>
            <p>Los resultados aparecerán cuando se ejecute el monitoreo.</p>
          </div>
        )}
      </section>
    </div>
  );
}

function formatRelative(value) {
  const difference = Date.now() - new Date(value).getTime();
  const minutes = Math.floor(difference / 60000);
  if (minutes < 60) return `Hace ${Math.max(1, minutes)} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Hace ${hours} h`;
  return `Hace ${Math.floor(hours / 24)} d`;
}
